import axios from 'axios';
import type {
  Catalog,
  UploadUrlResponse,
  PublishResponse,
  PackManifest,
  CatalogPatch,
  VersionHistory
} from './types';

const API_BASE = import.meta.env.VITE_API_BASE || '/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

export async function getCatalog(bustCache: boolean = false): Promise<Catalog> {
  // Add cache-busting parameter if requested
  const url = bustCache ? `/packs?_t=${Date.now()}` : '/packs';
  const response = await api.get<Catalog>(url, {
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  });
  return response.data;
}

export async function getUploadUrl(
  path: string,
  contentType: 'image/webp' | 'application/json' | 'font/ttf' | 'font/woff2' | 'video/mp4'
): Promise<UploadUrlResponse> {
  const response = await api.post<UploadUrlResponse>('/uploadUrl', {
    path,
    contentType
  });
  return response.data;
}

export async function uploadFile(
  uploadUrl: string,
  file: File | Blob,
  headers: Record<string, string>
): Promise<void> {
  // Use POST method for the upload endpoint
  await axios.post(uploadUrl, file, {
    headers: {
      ...headers,
      'Content-Type': headers['Content-Type'] || 'application/octet-stream'
    },
    maxContentLength: Infinity,
    maxBodyLength: Infinity
  });
}

export async function publish(
  packId: string,
  newVersion: number,
  manifest: PackManifest,
  catalogPatch: CatalogPatch
): Promise<PublishResponse> {
  const response = await api.post<PublishResponse>('/publish', {
    packId,
    newVersion,
    manifest,
    catalogPatch
  });
  return response.data;
}

export async function healthCheck(): Promise<{ status: string; timestamp: string }> {
  const response = await api.get('/health');
  return response.data;
}

export async function getManifest(packId: string, version: number, bustCache: boolean = false): Promise<PackManifest> {
  const url = bustCache
    ? `/manifest/${packId}/${version}?_t=${Date.now()}`
    : `/manifest/${packId}/${version}`;

  const response = await api.get<PackManifest>(url, {
    headers: bustCache ? {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    } : {}
  });
  return response.data;
}

export async function getManifestFromUrl(manifestUrl: string): Promise<PackManifest> {
  const response = await axios.get<PackManifest>(manifestUrl);
  return response.data;
}

// Version History
export async function getVersionHistory(): Promise<VersionHistory[]> {
  const response = await api.get<VersionHistory[]>('/history');
  return response.data;
}

export async function getVersionHistoryByPack(packId: string): Promise<VersionHistory[]> {
  const response = await api.get<VersionHistory[]>(`/history/${packId}`);
  return response.data;
}

// Toggle Pack Status (Active/Inactive)
export async function togglePackStatus(packId: string, active: boolean): Promise<void> {
  await api.patch(`/packs/${packId}/status`, { active });
}

// Delete files from R2 storage
export async function deleteFiles(urls: string[], packId: string, version: number): Promise<{
  success: boolean;
  deletedCount: number;
  failedCount: number;
  deletedFiles: string[];
  failedFiles: string[];
  manifestUpdated: boolean;
  itemsRemoved: number;
}> {
  const response = await api.post('/files/delete', { urls, packId, version });
  return response.data;
}

// Permanently delete a pack/bucket and all its files
export async function deletePack(packId: string): Promise<{
  success: boolean;
  packId: string;
  filesDeleted: number;
  catalogVersion: number;
}> {
  const response = await api.delete(`/packs/${packId}`);
  return response.data;
}
